import { useCallback, useEffect, useState } from 'react';
import { api, type ExportRecord, type ExportStatus } from '../api/client.ts';
import { t, type StringKey } from '../i18n/index.ts';
import { formatClockSeconds, formatBytes, formatRemaining, formatDuration } from '../lib/format.ts';
import { useExportEvents } from '../lib/events.ts';

/** Fallback refresh, in case the event stream is blocked (spec §8). */
const POLL_MS = 10_000;

const STATUS_LABEL: Record<ExportStatus, StringKey> = {
  queued: 'export.queued',
  processing: 'export.processing',
  ready: 'export.ready',
  failed: 'export.failed',
  purged: 'export.purged',
};

interface Props {
  timeZone: string;
}

export function ExportsTable({ timeZone }: Props): React.ReactElement {
  const [rows, setRows] = useState<ExportRecord[] | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setRows(await api.exports());
    } catch {
      // Leave whatever is on screen rather than blanking it on a blip.
    }
  }, []);

  useEffect(() => {
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // A change to any export is a reason to re-read the list, which keeps one
  // code path for what the rows contain.
  const onUpdate = useCallback(() => { void refresh(); }, [refresh]);
  useExportEvents(onUpdate);

  if (rows === null) return <div className="empty">{t('player.loading')}</div>;
  if (rows.length === 0) return <div className="empty">{t('empty.no_exports')}</div>;

  return (
    <section>
      <div className="grid-head">
        <h2>{t('exports.title')}</h2>
        <span className="count m">{rows.length}</span>
      </div>
      {/* Said where the files are, because they do not stay (D84). */}
      <p className="page-note">{t('exports.cleared_daily')}</p>

      <div className="table-scroll">
        <table className="exports">
          <thead>
            <tr>
              <th>{t('exports.filename')}</th>
              <th>{t('exports.range')}</th>
              <th>{t('editor.duration')}</th>
              <th>{t('exports.size')}</th>
              <th>{t('exports.status')}</th>
              <th>{t('exports.downloads')}</th>
              <th>{t('exports.expiry')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const expiresIn = new Date(row.expires_at).getTime() - now;
              return (
                <tr key={row.id}>
                  <td>{row.label?.trim() || <span className="muted">{t('export.untitled')}</span>}</td>
                  {/* A span of clock time is an axis: left to right. */}
                  {/* Seconds matter here: a 33-second cut and the minute it
                      sits in are not the same thing to a producer. */}
                  <td className="m" dir="ltr">
                    {formatClockSeconds(row.range_start_at, timeZone)}
                    {' – '}
                    {formatClockSeconds(row.range_end_at, timeZone)}
                  </td>
                  <td className="m" dir="ltr">{formatDuration(row.duration_sec)}</td>
                  <td className="m" dir="ltr">
                    {row.file_size_bytes === null ? '—' : formatBytes(row.file_size_bytes)}
                  </td>
                  <td>
                    <span className={`chip export-${row.status}`}>
                      {t(STATUS_LABEL[row.status])}
                    </span>
                    {row.status === 'failed' && row.error_message && (
                      <span className="muted"> {row.error_message}</span>
                    )}
                  </td>
                  {/* What was actually taken, from the audit trail. */}
                  <td>
                    {row.download_count > 0 ? (
                      <>
                        <span className="m" dir="ltr">
                          {t('exports.downloaded_times', { count: row.download_count })}
                        </span>
                        {row.last_downloaded_at && (
                          <span className="muted m" dir="ltr">
                            {' '}
                            {formatClockSeconds(row.last_downloaded_at, timeZone)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="muted">{t('exports.not_downloaded')}</span>
                    )}
                  </td>
                  <td className="m" dir="ltr">
                    {row.status === 'ready' && expiresIn > 0
                      ? t('export.expires_in', { time: formatRemaining(expiresIn) })
                      : '—'}
                  </td>
                  <td>
                    {row.download_url && (
                      // A plain link, so the browser handles the download and
                      // honours the filename the server sends.
                      <a className="download-link" href={row.download_url} download>
                        {t('action.download')}
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
